import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { db, storage } from "../../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  doc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { toast } from "react-hot-toast";

/* ───────── constants ───────── */
const MAX_MB = 15;
const ACCEPTED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
];

/* ───────── helpers ───────── */
function currencySym(code) {
  switch (code) {
    case "LKR":
      return "Rs";
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "INR":
      return "₹";
    default:
      return code || "$";
  }
}
const pad2 = (n) => String(n).padStart(2, "0");
const tsMs = (x) =>
  x?.toDate?.()?.getTime?.() || (x ? new Date(x).getTime() : 0);
const rankTicket = (t) =>
  Math.max(
    tsMs(t.updatedAt),
    tsMs(t.paymentSubmittedAt),
    tsMs(t.agentApprovedAt),
    tsMs(t.requestedAt),
    tsMs(t.createdAt)
  );

/* ───────── page ───────── */
export default function PayBundle({ user }) {
  const { bundleKey: raw } = useParams();
  const navigate = useNavigate();
  const bundleKey = useMemo(() => {
    try {
      return decodeURIComponent(raw || "");
    } catch {
      return raw || "";
    }
  }, [raw]);

  const [tickets, setTickets] = useState([]); // agent_approved only
  const [loading, setLoading] = useState(true);

  // proof inputs
  const [fileObj, setFileObj] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [slipUrl, setSlipUrl] = useState("");
  const [refId, setRefId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Agent banking fallback (when tickets don't embed them)
  const [agentMeta, setAgentMeta] = useState({
    banks: [],
    binance: "",
    country: "",
    district: "",
    name: "",
  });

  // subscribe bundle tickets (works for stored bundleId and derived "agent|userKey")
  useEffect(() => {
    if (!bundleKey) return;
    setLoading(true);

    let agentPart = null,
      userPart = null;
    if (bundleKey.includes("|")) {
      const [a, u] = bundleKey.split("|");
      agentPart = a || null;
      userPart = u || null;
    }

    const acc = new Map(); // id -> ticket (keep freshest)
    const apply = (snap) => {
      snap.forEach((d) => {
        const t = { id: d.id, ...d.data() };
        const prev = acc.get(t.id);
        if (!prev || rankTicket(t) >= rankTicket(prev)) acc.set(t.id, t);
      });
      const arr = Array.from(acc.values());
      setTickets(arr);
      setLoading(false);
    };

    const unsubs = [];

    // 1) direct bundleId
    unsubs.push(
      onSnapshot(
        query(
          collection(db, "tickets"),
          where("bundleId", "==", bundleKey),
          where("status", "==", "agent_approved")
        ),
        apply,
        (e) => {
          console.error(e);
          toast.error("Bundle load failed");
        }
      )
    );

    // 2/3/4) derived combos for older tickets (no bundleId stored)
    if (agentPart && userPart) {
      const base = (field, value) =>
        query(
          collection(db, "tickets"),
          where("agentUid", "==", agentPart),
          where(field, "==", value),
          where("status", "==", "agent_approved")
        );
      ["userUid", "userMobileE164", "userMobile"].forEach((f) => {
        unsubs.push(onSnapshot(base(f, userPart), apply, () => {}));
      });
    }

    return () => unsubs.forEach((u) => u && u());
  }, [bundleKey]);

  // fetch agent banking fallback if needed (look at first ticket's agentUid)
  useEffect(() => {
    const aUid = tickets[0]?.agentUid;
    if (!aUid) return;

    let needFallback = false;
    for (const t of tickets) {
      if (!Array.isArray(t.agentBanks) || !t.agentBanks.length) {
        needFallback = true;
        break;
      }
    }
    if (!needFallback) {
      // still set name/country if present on ticket
      setAgentMeta((m) => ({
        ...m,
        name: tickets[0]?.agentName || m.name,
        country: tickets[0]?.agentCountry || m.country,
        district: tickets[0]?.agentDistrict || m.district,
      }));
      return;
    }

    (async () => {
      try {
        const s = await getDoc(doc(db, "agents", aUid));
        if (!s.exists()) return;
        const a = s.data() || {};
        setAgentMeta({
          banks: Array.isArray(a.banks) ? a.banks : [],
          binance: a.binancePayId || "",
          country: a.country || "",
          district: a.district || "",
          name: a.fullName || a.username || a.name || a.email || "",
        });
      } catch (e) {
        console.error(e);
      }
    })();
  }, [tickets]);

  // derived totals + earliest expiry + currency
  const currency = tickets[0]?.currency || "LKR";
  const sym = currencySym(currency);
  const totals = useMemo(() => {
    let nums = 0,
      amount = 0,
      earliestExpire = null;
    for (const t of tickets) {
      const n = Array.isArray(t.numbers) ? t.numbers.length : 0;
      const unit = Number(t.unitPrice || 0);
      const amt = Number(t.amount || unit * n);
      nums += n;
      amount += amt;
      const exp =
        t.paymentExpireAt?.toDate?.() ||
        (t.paymentExpireAt ? new Date(t.paymentExpireAt) : null);
      if (exp && (!earliestExpire || exp < earliestExpire))
        earliestExpire = exp;
    }
    return { nums, amount, earliestExpire };
  }, [tickets]);

  // countdown badge
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const badge = useMemo(() => {
    if (!totals.earliestExpire) return "";
    const left = totals.earliestExpire.getTime() - now;
    if (left <= 0) return "Time up";
    const m = Math.floor(left / 60000);
    const s = Math.floor((left % 60000) / 1000);
    return `Pay within ${pad2(m)}:${pad2(s)}`;
  }, [totals.earliestExpire, now]);
  const expired =
    totals.earliestExpire && totals.earliestExpire.getTime() <= now;

  /* ───────── upload helpers ───────── */
  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ACCEPTED_TYPES.includes(f.type))
      return toast.error("Only images or PDF allowed.");
    if (f.size > MAX_MB * 1024 * 1024)
      return toast.error(`File too large. Max ${MAX_MB}MB`);
    setFileObj(f);
  };

  const doUploadIfNeeded = async () => {
    if (!fileObj) return slipUrl || "";
    const ext = fileObj.name.split(".").pop()?.toLowerCase() || "dat";
    // store under the first ticket's folder (matches your single payment page rules)
    const firstTicketId = tickets[0]?.id || "bundle";
    const path = `tickets/${firstTicketId}/bundle_${Date.now()}.${ext}`;
    const r = ref(storage, path);
    const task = uploadBytesResumable(r, fileObj, {
      contentType: fileObj.type,
      cacheControl: "public,max-age=31536000",
    });
    return new Promise((resolve, reject) => {
      task.on(
        "state_changed",
        (s) =>
          setUploadPct(Math.round((s.bytesTransferred / s.totalBytes) * 100)),
        (err) => reject(err),
        async () => resolve(await getDownloadURL(r))
      );
    });
  };

  async function submitPayment() {
    if (!tickets.length) return toast.error("No tickets to pay.");
    if (expired) return toast.error("Time up. Your numbers were released.");
    if (!fileObj && !slipUrl && !refId)
      return toast.error("Upload a slip or enter a reference ID.");

    try {
      setSubmitting(true);
      const url = await doUploadIfNeeded();

      // mark every ticket in this bundle as payment_submitted
      await Promise.all(
        tickets.map((t) =>
          updateDoc(doc(db, "tickets", t.id), {
            status: "payment_submitted",
            paymentSubmittedAt: serverTimestamp(),
            paymentProofUrl: url || "",
            referenceId: refId || "",
            updatedAt: serverTimestamp(),
          })
        )
      );
      toast.success("Payment submitted. Await agent approval.");
      navigate("/my-tickets", { replace: true });
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  }

  /* ───────── render ───────── */
  if (loading) {
    return (
      <div className="max-w-3xl mx-auto mt-8 md:mt-16 p-4">
        <div className="animate-pulse h-8 w-52 bg-slate-700 rounded mb-4" />
        <div className="animate-pulse h-40 bg-slate-800 rounded-2xl" />
      </div>
    );
  }

  const banks = tickets[0]?.agentBanks?.length
    ? tickets[0].agentBanks
    : agentMeta.banks;
  const binance = tickets[0]?.agentBinancePayId || agentMeta.binance || "";
  const agentName =
    tickets[0]?.agentName ||
    agentMeta.name ||
    tickets[0]?.agentUid?.slice(0, 6) ||
    "Agent";
  const country = tickets[0]?.agentCountry || agentMeta.country || "";
  const district = tickets[0]?.agentDistrict || agentMeta.district || "";

  return (
    <div className="max-w-3xl mx-auto mt-8 md:mt-16 p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Bundle Payment</h1>
        <Link
          to="/my-tickets"
          className="px-3 py-1.5 rounded-xl bg-slate-800 ring-1 ring-slate-700 hover:bg-slate-700 text-sm"
        >
          My Tickets
        </Link>
      </div>

      {!tickets.length ? (
        <div className="mt-4 p-4 rounded-2xl bg-slate-800 ring-1 ring-slate-700 text-slate-300">
          This bundle has no pending tickets.
        </div>
      ) : (
        <div className="mt-4 p-4 rounded-2xl bg-slate-800 ring-1 ring-slate-700">
          {/* status */}
          {expired ? (
            <div className="p-3 mb-4 rounded bg-red-900/40 text-red-300">
              Time up. Your numbers were released.
            </div>
          ) : badge ? (
            <div className="p-3 mb-4 rounded bg-yellow-900/40 text-yellow-300">
              {badge}
            </div>
          ) : null}

          {/* agent */}
          <div className="mb-4">
            <div className="text-sm text-slate-300">Agent</div>
            <div className="text-lg font-semibold">
              {agentName}
              {(district || country) && (
                <span className="text-sm text-slate-300">
                  {" "}
                  · {district || "-"}, {country || "-"}
                </span>
              )}
            </div>
          </div>

          {/* payment methods */}
          <div className="space-y-3 mb-4">
            <div className="grid gap-3">
              {banks.map((b, i) => (
                <div
                  key={(b._id || i) + (b.accountNumber || i)}
                  className="p-3 rounded bg-slate-700"
                >
                  <div className="text-sm text-slate-300">Bank {i + 1}</div>
                  <div className="font-semibold">{b.bankName}</div>
                  <div className="text-sm">
                    {b.accountName} · {b.accountNumber}
                    {b.branch ? ` · ${b.branch}` : ""}
                  </div>
                </div>
              ))}
              {!banks.length && (
                <div className="p-3 rounded bg-slate-700 text-sm text-slate-300">
                  No bank details provided yet. Please contact the agent.
                </div>
              )}
            </div>
            {!!binance && (
              <div className="p-3 rounded bg-slate-700">
                <div className="text-sm text-slate-300">Binance Pay ID</div>
                <div className="font-semibold">{binance}</div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-3">
              <div className="p-3 rounded bg-slate-700">
                <div className="text-sm text-slate-300">Total amount</div>
                <div className="font-semibold">
                  {sym}
                  {totals.amount.toFixed(2)}
                </div>
              </div>
              <div className="p-3 rounded bg-slate-700">
                <div className="text-sm text-slate-300">Numbers total</div>
                <div className="font-semibold">{totals.nums}</div>
              </div>
            </div>
          </div>

          {/* upload / inputs */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-300">
                Proof file (image/PDF)
              </label>
              <input
                type="file"
                accept=".pdf,image/*"
                className="w-full mt-2 file:mr-3 file:px-3 file:py-2 file:rounded file:bg-slate-600 file:text-white file:border-0 file:cursor-pointer bg-slate-700 text-white p-1 rounded"
                onChange={onPickFile}
                disabled={submitting || expired}
              />
              {fileObj && (
                <div className="text-xs text-slate-300 mt-1">
                  {fileObj.name} · {(fileObj.size / (1024 * 1024)).toFixed(2)}{" "}
                  MB {submitting && uploadPct ? `· ${uploadPct}%` : ""}
                </div>
              )}
            </div>

            <div>
              <label className="text-sm text-slate-300">
                OR paste proof URL
              </label>
              <input
                className="w-full mt-2 px-3 py-2 rounded bg-slate-700 text-white"
                value={slipUrl}
                onChange={(e) => setSlipUrl(e.target.value)}
                placeholder="https://…"
                disabled={submitting || expired}
              />
              <div className="mt-4">
                <label className="text-sm text-slate-300">
                  Reference/Slip ID
                </label>
                <input
                  className="w-full mt-2 px-3 py-2 rounded bg-slate-700 text-white"
                  value={refId}
                  onChange={(e) => setRefId(e.target.value)}
                  placeholder="Transaction reference"
                  disabled={submitting || expired}
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={submitPayment}
              className="px-4 py-2 rounded-xl bg-lime-400 text-black font-semibold disabled:opacity-50"
              disabled={expired || submitting}
            >
              {submitting ? "Submitting…" : "Submit Payment"}
            </button>
            <Link
              to="/my-tickets"
              className="px-4 py-2 rounded-xl bg-slate-700"
            >
              Back to Tickets
            </Link>
          </div>

          {/* simple per-ticket preview */}
          <div className="mt-6 max-h-64 overflow-auto space-y-2 text-sm">
            {tickets.map((t) => (
              <div
                key={t.id}
                className="px-3 py-2 rounded-xl bg-slate-900 ring-1 ring-slate-700"
              >
                <div className="flex items-center justify-between">
                  <div className="font-semibold">
                    Card #{String(t.cardId).slice(0, 8)}…
                  </div>
                  <div className="text-slate-300">
                    {sym}
                    {Number(
                      t.amount || (t.unitPrice || 0) * (t.numbers?.length || 0)
                    ).toFixed(2)}
                  </div>
                </div>
                <div className="text-slate-300 font-mono truncate">
                  {Array.isArray(t.numbers) ? t.numbers.join(", ") : "-"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
